<template>
	<DashCard
		title="Production"
		:loading="loading"
		:has-error="hasError"
		:no-data="noLoadedData"
		:info-data="infoData"
		info-data-title="Accelerate: Production"
		style="min-height:100%;"
		@reload="$emit('reload', 'accelerate_production')"
	>
		<template #icon><Icon /></template>

		<div class="card-content" :class="isMobileView && 'card-content--mobile'">
			<v-data-table
				:headers="headers"
				:items="computedData"
				item-key="key"
				mobile-breakpoint="0"
				class="card-table"
				dark hide-default-footer hide-default-header disable-sort
			>
				<template #item.metric="{ item }">
					<span class="nowrap"><strong>{{ item.metric }}</strong></span>
				</template>
				<template #item.value="{ item }">
					<span v-if="item.type === 'currency'">
						<span v-if="item.value < 0">-</span>
						<span class="with-currency">{{ formatCurrency(item.value) }}</span>
					</span>
					<span v-else class="nowrap">{{ formatValue(item.value) }}</span>
				</template>
			</v-data-table>
		</div>
	</DashCard>
</template>

<script>
import DashCard from '../DashCard.vue'
import Icon from '../../Icons/ProductionTrackingIcon.vue'

const config = [
	{ key: 'personal_production', metric: 'Personal Production', type: 'currency' },
	{ key: 'agency_production', metric: 'Agency Production', type: 'currency' },
	{ key: 'total_production', metric: 'Total Production', type: 'currency' },
	{ key: 'unique_writers', metric: 'Unique Writers' },
]

export default {
    components: {
		DashCard,
		Icon,
	},
	props: {
		data: {
			type: Object,
			default: null,
		},
		infoData: Object,
		monthlyPeriod: {
			type: Boolean,
			default: true,
		},
	},
    computed: {
		isMobileView () {
			return this.$vuetify.breakpoint.xs
		},
		headers () {
			return [{
                text: 'Metric',
                value: 'metric',
            }, {
                text: 'Value',
                value: 'value',
			}]
		},
		loading () {
			if (!this.data) { return false }
			return this.data.loading
		},
		hasError () {
			if (!this.data) { return false }
			return this.data.error
		},
		noLoadedData () {
			if (this.data && this.data.loaded) {
				if (!this.data.data) { return true }
				return Object.values(this.data.data).every(this.checkNullish)
			}
			return false
		},
		computedData () {
			if (this.data && this.data.loaded && !this.noLoadedData) {
				const { data } = this.data
				const monthly = config.map(item => this.getDataItem(item, data, 'monthly'))
				const weekly = config.map(item => this.getDataItem(item, data, 'weekly'))
				return this.monthlyPeriod ? monthly : weekly
			}
			return config.map(item => ({ ...item, value: '' }))
		},
    },
	methods: {
		checkNullish (value) {
			return [undefined, null, ''].includes(value)
		},
		formatValue (value) {
			if (this.checkNullish(value)) { return 'NA' }
			return value
		},
		formatCurrency (value) {
			if (this.checkNullish(value)) { return 'NA' }
			return this.formatNumber(Math.abs(value), '(0,0)')
		},
		getDataItem (item, data, period) {
			if (item.key === 'total_production') {
				const personalProduction = data[`personal_production_${period}`]
				const agencyProduction = data[`agency_production_${period}`]
				const totalProduction = this.checkNullish(personalProduction) || this.checkNullish(agencyProduction)
					? undefined
					: personalProduction + agencyProduction
				return { ...item, value: totalProduction }
			} else {
				return { ...item, value: data[`${item.key}_${period}`] }
			}
		},
	},
}
</script>

<style lang="scss" scoped>
@import '../../style/index.scss';

$block: card-content;

.#{$block} {
	color: $color-text;

	--td-padding: 0 1rem;

	&--mobile {
		--td-padding: 0 0.75rem;
	}

	.card-table {
		$border-bottom: 1px solid $color-border !important;

		background-color: transparent;
		color: inherit;
		border-radius: 0;

		::v-deep tbody tr td {
			height: 36px;
			border-bottom: $border-bottom;
			padding: var(--td-padding);

			&:first-child {
				width: 8rem;
				background-color: $color-water-green-20;
			}
		}
	}
}
</style>
